/*
  Predictive Search with Vault Only mode (page specific)

  How it works
  1) On your Vault page search form ONLY, include:
       <input type="hidden" name="vault_only" value="1">
       <input type="hidden" name="collection" value="the-vault">

  2) This file will:
     - Append those params to the predictive request (/search/suggest) only for that form
     - Prevent cache collisions between vault and full search
     - Filter the predictive dropdown products to ONLY products in that collection
       using /collections/{handle}/products.json (no tags needed)
     - Remove the Suggestions group (queries and collections) for vault mode
       and optionally remove Pages/Articles and the "Search for" row in vault mode

  Notes
  - This assumes your theme has SearchForm and routes.predictive_search_url (Dawn style).
  - Keep your predictive-search.liquid as the normal theme version (do not filter there).
*/

class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Cache for collection handle sets:
    // { [collectionHandle]: Promise<Set<string>> }
    this._collectionHandleSets = {};

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    const currentText = el?.innerText;
    if (!currentText) return;

    const prev = String(previousTerm || '');
    const next = String(newTerm || '');

    if (prev && currentText.match(new RegExp(prev, 'g'))?.length > 1) return;

    el.innerText = currentText.replace(prev, next);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  /*
    Vault mode is enabled only when this search input's form includes:
      vault_only=1 and collection=<handle>
  */
  getScopedPredictiveParams() {
    const params = new URLSearchParams();

    const form = this.input?.form;
    if (!form) return params;

    const vaultOnlyInput = form.querySelector('input[name="vault_only"]');
    const collectionInput = form.querySelector('input[name="collection"]');

    const vaultOnly = vaultOnlyInput ? String(vaultOnlyInput.value || '').trim() : '';
    const collection = collectionInput ? String(collectionInput.value || '').trim() : '';

    if (vaultOnly === '1' && collection.length) {
      params.set('vault_only', '1');
      params.set('collection', collection);
    }

    return params;
  }

  isVaultMode(scopedParams) {
    return scopedParams?.get('vault_only') === '1' && !!scopedParams?.get('collection');
  }

  /*
    Removes Suggestions and optional non product blocks for vault mode.
    This edits the rendered predictive HTML in the dropdown.
  */
  pruneNonProductGroupsForVault() {
    const root = this.querySelector('#predictive-search-results');
    if (!root) return;

    // Suggestions (queries and collections)
    const suggestionsHeading = root.querySelector('#predictive-search-queries');
    if (suggestionsHeading) {
      const wrapper = suggestionsHeading.closest('div');
      if (wrapper) wrapper.remove();
    }

    // Pages and Articles (desktop)
    const pagesDesktopHeading = root.querySelector('#predictive-search-pages-desktop');
    if (pagesDesktopHeading) {
      const wrapper = pagesDesktopHeading.closest('.predictive-search__pages-wrapper');
      if (wrapper) wrapper.remove();
    }

    // Pages and Articles (mobile)
    const pagesMobileHeading = root.querySelector('#predictive-search-pages-mobile');
    if (pagesMobileHeading) {
      const wrapper = pagesMobileHeading.closest('.predictive-search__pages-wrapper');
      if (wrapper) wrapper.remove();
    }

    // Optional: remove the "Search for" row
    const searchForRow = root.querySelector('#predictive-search-option-search-keywords');
    if (searchForRow) searchForRow.remove();

    // If any now empty groups remain, remove them
    const groups = Array.from(root.querySelectorAll('.predictive-search__result-group'));
    for (const group of groups) {
      const hasProducts = !!group.querySelector('#predictive-search-results-products-list');
      const hasAnyItems = !!group.querySelector('li');
      if (!hasProducts && !hasAnyItems) group.remove();
    }
  }

  /*
    Fetch and cache a Set of product handles belonging to a collection.
    Uses /collections/{handle}/products.json?limit=250 and follows Link rel="next" when present.
  */
  getCollectionHandleSet(collectionHandle) {
    if (!collectionHandle) return Promise.resolve(new Set());

    if (this._collectionHandleSets[collectionHandle]) {
      return this._collectionHandleSets[collectionHandle];
    }

    this._collectionHandleSets[collectionHandle] = this.fetchCollectionHandles(collectionHandle);
    return this._collectionHandleSets[collectionHandle];
  }

  async fetchCollectionHandles(collectionHandle) {
    const handles = new Set();

    let url = `${window.location.origin}/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`;

    for (let safety = 0; safety < 20; safety++) {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) break;

      const data = await res.json();
      const products = Array.isArray(data?.products) ? data.products : [];

      for (const p of products) {
        if (p && p.handle) handles.add(String(p.handle));
      }

      const link = res.headers.get('link') || res.headers.get('Link');
      if (!link) break;

      const nextUrl = this.parseNextLinkHeader(link);
      if (!nextUrl) break;

      url = nextUrl;
    }

    return handles;
  }

  parseNextLinkHeader(linkHeader) {
    const parts = String(linkHeader).split(',');
    for (const part of parts) {
      const section = part.trim();
      if (!section.includes('rel="next"')) continue;

      const match = section.match(/<([^>]+)>/);
      if (match && match[1]) return match[1];
    }
    return null;
  }

  extractProductHandleFromUrl(href) {
    try {
      const u = new URL(href, window.location.origin);
      const m = u.pathname.match(/^\/products\/([^\/?#]+)/);
      return m ? m[1] : null;
    } catch (e) {
      return null;
    }
  }

  /*
    DOM filter: remove product list items whose /products/{handle} is not in the vault collection set.
  */
  async filterRenderedResultsToCollection(collectionHandle) {
    const handleSet = await this.getCollectionHandleSet(collectionHandle);

    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const items = Array.from(list.querySelectorAll('li.predictive-search__list-item'));
    let kept = 0;

    for (const li of items) {
      const a = li.querySelector('a[href]');
      const href = a ? a.getAttribute('href') : '';
      const handle = href ? this.extractProductHandleFromUrl(href) : null;

      if (!handle || !handleSet.has(handle)) {
        li.remove();
      } else {
        kept++;
      }
    }

    // If no products remain, remove the products heading block so the dropdown does not look broken
    if (kept === 0) {
      const productsHeading = this.querySelector('#predictive-search-products');
      if (productsHeading) {
        const wrapper = productsHeading.closest('div');
        if (wrapper) wrapper.remove();
      }
    }
  }

  getSearchResults(searchTerm) {
    const normalizedTerm = String(searchTerm || '').trim().toLowerCase();

    const scopedParams = this.getScopedPredictiveParams();
    const scopedQueryString = scopedParams.toString();
    const scopeKey = scopedQueryString ? `|${scopedQueryString}` : '|full';

    // Prevent vault vs full cache collisions
    const queryKey = `${normalizedTerm}${scopeKey}`;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], scopedParams);
      return;
    }

    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', 'predictive-search');

    if (scopedQueryString) {
      scopedParams.forEach((value, key) => url.searchParams.set(key, value));
    }

    fetch(url.toString(), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';

        // Sync cache across all predictive-search instances
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup, scopedParams);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, scopedParams) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    if (this.isVaultMode(scopedParams)) {
      // Remove suggestions and other non product groups for vault mode
      this.pruneNonProductGroupsForVault();

      const collectionHandle = scopedParams.get('collection');

      this.filterRenderedResultsToCollection(collectionHandle).then(() => {
        this.setLiveRegionResults();
        this.open();
      });

      return;
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const text = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '';
    this.setLiveRegionText(text);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}